/**
 * Visual tokens for the coding-screen shell and its molecules. Single source of
 * truth for the shell's presentational chrome -- background/border/radius/shadow colors and the
 * fixed-geometry heights/rail-width every header bar, pane card, and strip renders at. Every
 * molecule/organism/template listed in the ticket imports its chrome from here instead of hand-coding
 * its own copy, so there is exactly one place to retune a value.
 *
 * Matches `docs/tickets/CDMX-18286/assets/empty-ui.png` (the approved empty-shell mockup) as closely
 * as a screenshot can be read by eye. EXACT VALUES ARE GUESSES pending a browser visual-comparison
 * pass -- this pass centralizes and re-tunes them from the earlier per-component scatter (where, before this change,
 * `PatientSummaryBar` hard-coded 56px and `DocumentTitleNav`/`ReviewTransactionsTabs` hard-coded 44px
 * -- the reverse of the mockup reading below), but a future browser pass may still adjust any of these
 * further. Nothing here is guaranteed pixel-final.
 *
 * Plain string/number literals only -- no functions, no theme references, no React types -- so this
 * file imports NOTHING and trivially satisfies the project_starburst isolation boundary guard, the
 * same "this file imports nothing" precedent `config/schema.ts` sets. STILL TRUE after the SECOND
 * styling pass below (rem units + biome CSS-variable colors): a `var(--color-x, #fallback)` reference
 * is a plain CSS function embedded in an ordinary string literal -- there is no import, no `url()`, no
 * `@import`/`@use`, nothing the AST-import boundary guard's file-reference walk (`__tests__/
 * boundary.test.ts`) recognizes as a module reference at all. It is exactly as inert to that guard as
 * the hex strings it replaces.
 *
 * WHY STRINGS, NOT BARE NUMBERS: every length below is a CSS string like `'2.5rem'` (formerly
 * `'40px'`), never a bare number. Chakra style props resolve a *number* against the theme's
 * `space`/`sizes` scale first (e.g. `theme.sizes[44]` is `'11rem'` -- 176px -- in the default theme,
 * not 44px), so a bare `44` passed to `h`/`w`/`minH` would silently resolve to the WRONG value. A
 * string like `'2.5rem'` does not match any scale key and is used literally -- the same protection a
 * px string had; only the unit changed. Every consumer in this folder already follows this rule (see
 * e.g. the pre-existing `DOCUMENT_TITLE_NAV_HEIGHT = '44px'` precedent); these tokens preserve it.
 *
 * SECOND STYLING PASS (dynamic units + theme color variables):
 *
 * 1. REM, NOT PX, FOR GEOMETRY: `TOPBAR_H`/`CASE_CONTEXT_BAR_H`/`FOOTER_H`/
 *    `CARD_RADIUS`/`RAIL_WIDTH` are now `rem` strings (`px / 16`, this repo's -- and every browser's
 *    default -- root font-size), so the shell's fixed chrome scales with the user's root font-size
 *    (browser zoom / OS accessibility text-size settings) instead of staying pixel-locked. `GUTTER_PX`
 *    is one deliberate holdout -- see its own doc comment below for why converting it is riskier
 *    than it looks and out of scope for this pass.
 *
 *    CAVEAT, discovered during the tab-styling pass: the `px / 16` arithmetic above is WRONG for this
 *    app. `src/styles/cmx.scss` forces a 14px root, so every token converted with ÷16 paints 12.5%
 *    SHORT of its documented size (`TOPBAR_H`'s "40px" renders 35px, `CASE_CONTEXT_BAR_H`'s "40px"
 *    likewise, and so on) -- the same trap `HOLD_RADIUS`/`POPOVER_RADIUS` already call out and avoid
 *    by staying px. `STRIP_H` and `PATIENT_BAR_H` have been moved to absolute px (see their own
 *    comments); the remaining rem tokens above still carry the discrepancy and should be revisited
 *    in a pass that can re-check each against the mockup, which this tab-focused pass deliberately
 *    does not do. Chakra's own spacing-SCALE tokens (`SHELL_PAD`/`REGION_GAP`) were already relative
 *    before this pass (Chakra maps a scale int to a `rem` value internally) and pane `sizePct` was already a `%` -- neither needed to change here. No component
 *    in this folder hard-codes an explicit Chakra `fontSize` in px today (checked across every
 *    molecule/organism/template) -- text sizing already comes from Chakra's theme `rem` scale, so
 *    there was nothing to convert on the font side.
 *
 * 2. THEME CSS VARIABLES FOR COLOR, WITH A HEX FALLBACK: every color token below is now
 *    `var(--color-<biome-token>, <original-hex>)` instead of a bare hex literal. `ui-components/dist/
 *    index.css` (loaded once, app-wide, outside this folder) defines these `--color-*` custom
 *    properties on `:root` from `@codametrix/design-tokens`'s biome palette
 *    (`packages/design-tokens/dist/css/biome/colors.css` in the ui-omnibus monorepo) -- so the real
 *    app picks up the platform's actual theme color the instant that stylesheet is present. The
 *    literal hex second argument is CSS's own `var()` fallback: whenever the custom property is
 *    UNDEFINED anywhere up the cascade (Storybook without the tokens stylesheet, a bare
 *    `<ChakraProvider>` unit test, jsdom -- none of which load `ui-components/dist/index.css`),
 *    `var(--color-x, #fallback)` evaluates to exactly `#fallback` -- the SAME value this token held
 *    before this pass, byte-for-byte. Nothing downstream (Chakra style props, raw inline `style`
 *    objects, template-literal `border` shorthands) parses or validates the string -- every consumer
 *    just forwards it verbatim to a CSS property -- so `var(...)` is a drop-in replacement for a hex
 *    literal everywhere a hex literal worked. Nearest-token choices (verified against the real
 *    `colors.css` values, not guessed) are documented per-token below; a token with no close-enough
 *    match in the current biome palette (`CARD_SHADOW`) is left as a plain literal instead of forcing
 *    a bad match -- also noted at that token.
 */

// ---------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------

/** Page canvas -- light gray-lavender workspace background behind the whole shell. Nearest biome
 *  token: `--color-structural-300` (`#f2f8fa`), the Euclidean-nearest of the `structural`/`base`
 *  surface family to this token's original `#f3f3f7` (distance ~5.9 in per-channel-squared 0-255 RGB
 *  space -- imperceptibly close); kept in the `structural` family rather than the marginally closer
 *  `--color-chips-grey-50` (`#f0f2f4`, distance ~4.4) so every plain neutral surface in this file
 *  (`PAGE_BG`/`STRIP_BG`/`CARD_BG`/`BAR_BG`) draws from the same semantic "structural surface" token
 *  family. */
export const PAGE_BG = 'var(--color-structural-300, #f3f3f7)';

/** Top utility-bar strip's own background -- a faint lavender, distinct from the page canvas.
 *  `TopBarContainer` is `visibility:hidden` by default, which hides this
 *  background along with everything else in the container; the token still styles the strip
 *  correctly for whenever `header.topBar.visible` is `true`. Nearest biome token:
 *  `--color-chips-plum-50` (`#f3eaf2`) -- the palette's only lavender/plum family, and the choice
 *  that preserves this token's own doc comment above ("distinct from the page canvas"): the
 *  marginally closer `--color-chips-grey-50` (`#f0f2f4`, distance ~6.5 vs plum-50's ~7.8) is a
 *  neutral grey that would render this bar nearly identically to `PAGE_BG`'s own `structural-300`
 *  mapping, erasing the intentional lavender accent this bar is supposed to read as. */
export const TOPBAR_BG = 'var(--color-chips-plum-50, #efedf8)';

/** Patient-summary / case-context header bar background -- plain white. Nearest biome token:
 *  `--color-structural-0` (`#ffffff`) -- an EXACT match, not an approximation. */
export const BAR_BG = 'var(--color-structural-0, #ffffff)';

/** Bottom-border color shared by the patient-summary and case-context header bars. No biome token is
 *  a semantic "border" role -- the closest CANDIDATES by raw distance (`--color-chips-plum-50`,
 *  `--color-progress-300`) both shift this neutral grey hairline toward a visible pink/blue cast,
 *  which reads as a rendering mistake rather than an intentional accent on a 1px divider.
 *  `--color-chips-grey-50` (`#f0f2f4`) is the closest candidate that stays hue-neutral (grey in, grey
 *  out; distance ~15.4 vs plum-50's ~13.0) -- a deliberate hue-preservation-over-minimum-distance
 *  choice, applied consistently to `CARD_BORDER`/`STRIP_BORDER` below too, so all three hairlines
 *  share one genuinely neutral token instead of three different, individually "closest," hues. */
export const BAR_BORDER = 'var(--color-chips-grey-50, #e7e7ee)';

/** Background of the LABEL segment in a case-context field pill. Reuses `BAR_BORDER`'s hue-neutral
 *  grey rather than introducing a fourth grey, but stays a separate export because fill and hairline
 *  are different roles that only happen to share a value today. Fallback retuned to `#ededf0` to
 *  match the approved mock's `--panel-2` reference value exactly (was `#e7e7ee`, an imperceptibly
 *  close guess made before that reference was available). */
export const FIELD_LABEL_BG = 'var(--color-chips-grey-50, #ededf0)';

/** Background of the VALUE segment in a case-context field pill. */
export const FIELD_VALUE_BG = 'var(--color-structural-0, #ffffff)';

/** The pill's own OUTER border -- needs real contrast against the white page/value background, so it
 *  stays a deliberately darker literal rather than the near-invisible `FIELD_LABEL_DIVIDER` below.
 *
 *  A plain literal, following `CARD_SHADOW`'s precedent: the biome palette jumps straight from
 *  `--color-chips-grey-50` (#f0f2f4, too light) to `--color-chips-grey-500` (#45556e, far too dark
 *  for a hairline), so there is no honest token to point at. */
export const FIELD_SEGMENT_DIVIDER = '#a5a1ab';

/** Hairline BETWEEN the LABEL and VALUE segments of a case-context field pill -- distinct from
 *  `FIELD_SEGMENT_DIVIDER` above. The approved mock's own reference (`--border: #ececef`) sits almost
 *  on top of `FIELD_LABEL_BG` (`--panel-2: #ededf0`) on purpose: the inner seam is meant to read as
 *  nearly invisible, quiet chrome, not a visible accent -- only the pill's OUTER edge needs to pop
 *  against the page. */
export const FIELD_LABEL_DIVIDER = 'var(--color-chips-grey-50, #ececef)';

/** Label-segment type treatment for a case-context field pill (e.g. "PAYER", "FLAGS"), matching the
 *  approved mock's `.field-label`-equivalent class exactly: `font-size: 9.5px`, `font-weight: 700`,
 *  `letter-spacing: 0.06em`. Chakra's `Overline 1` text style (9.72px / 600 / 0) is close but not
 *  pixel-identical, so the label applies these as explicit overrides on top of it rather than
 *  patching the shared theme text style (used elsewhere -- `PatientInfo`/`PayerInfo` -- where the
 *  approximation is fine). */
export const FIELD_LABEL_FONT_SIZE = '9.5px';
export const FIELD_LABEL_FONT_WEIGHT = 700;
export const FIELD_LABEL_LETTER_SPACING = '0.06em';

/** Pane card surface background (`DocumentationPane` / `ReviewTransactionsPane`), and the left
 *  rail's own background (the mockup reads the rail as white, matching the card body, not the
 *  off-white strip above it). Nearest biome token: `--color-structural-0` (`#ffffff`) -- an EXACT
 *  match, same as `BAR_BG` (both are plain white in the mockup). */
export const CARD_BG = 'var(--color-structural-0, #ffffff)';

/** Pane card border color. Same hue-neutral reasoning as `BAR_BORDER` above --
 *  `--color-chips-grey-50` (`#f0f2f4`) over the numerically-closer-but-hue-shifting
 *  `--color-chips-plum-50`. */
export const CARD_BORDER = 'var(--color-chips-grey-50, #e3e3ea)';

/** Pane card corner radius. `10px` -> `0.625rem` (÷16) -- see the file header's "REM, NOT PX" note. */
export const CARD_RADIUS = '0.625rem';

/** Pane card drop shadow. Left as an absolute-px `rgba(...)` box-shadow, NOT converted to a `rem`
 *  length or a theme variable: (1) the biome `colors.css` palette read for this pass has no
 *  elevation/shadow token, only flat `--color-*` hex values -- a multi-part
 *  `offset-x offset-y blur-radius color` box-shadow isn't representable by a single color variable;
 *  (2) shadow blur/offset is a fine-detail decorative cue, not a layout dimension that needs to track
 *  root font-size/zoom the way a bar height or rail width does. */
export const CARD_SHADOW = '0 1px 3px rgba(16, 16, 40, 0.06)';

/** Off-white background shared by every in-card "quiet chrome band": the pane header strips
 *  (`DocumentTitleNav` / `ReviewTransactionsTabs`), the pane footer strip (`PaneFooterSummary`), and
 *  the outer page footer (`PageFooter`) -- the mockup reads all four as the same light off-white
 *  family (the page footer's own reading is a hair lighter, ~#f7f7fa vs ~#f9f9fc, close enough to be
 *  one token pending the browser pass rather than adding a near-duplicate). Nearest biome token:
 *  `--color-structural-100` (`#f7fdff`), closest of the `structural` surface family to this token's
 *  original `#f9f9fc` (distance ~5.4). */
export const STRIP_BG = 'var(--color-structural-100, #f9f9fc)';

/** Border color paired with `STRIP_BG` above -- also used for the `DocumentationPane` left rail's
 *  border-right, which reads as the same quiet hairline in the mockup. Same hue-neutral
 *  `--color-chips-grey-50` choice as `BAR_BORDER`/`CARD_BORDER` above (and here it also happens to be
 *  the single closest biome token of ANY family, distance ~6.2, not just the closest neutral one). */
export const STRIP_BORDER = 'var(--color-chips-grey-50, #ededf2)';

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/** Top utility bar's reserved height (built but hidden by default -- see `TopBarContainer`).
 *  `40px` -> `2.5rem` (÷16) -- see the file header's "REM, NOT PX" note. */
export const TOPBAR_H = '2.5rem';

/** Patient-summary header bar's reserved height. Absolute px, NOT a `rem`, for the reason
 *  `STRIP_H`/`HOLD_RADIUS` document: the app forces a 14px root (`src/styles/cmx.scss`), so the
 *  header's `px / 16` arithmetic paints 12.5% SHORT of the documented size -- the former `'2.75rem'`
 *  was labelled 44px and actually rendered 38.5px. Px keeps the bar at 48px on any root. */
export const PATIENT_BAR_H = '48px';

/** Case-context header bar's reserved height. `40px` -> `2.5rem` (÷16). */
export const CASE_CONTEXT_BAR_H = '3.5rem';

/** Pane header-strip height, shared by `DocumentTitleNav` and `ReviewTransactionsTabs` so the
 *  left/right strips read as one visual row.
 *
 *  ABSOLUTE PX, NOT REM -- the one deliberate exception to the file header's "REM, NOT PX" rule
 *  besides `GUTTER_PX`, for the same reason `HOLD_RADIUS` documents: the app forces a 14px root
 *  (`src/styles/cmx.scss`), so a token converted with the header's `px / 16` arithmetic paints
 *  12.5% SHORT of its documented size. This token's former `'3.5rem'` value was labelled 56px and
 *  actually rendered 49px. Retuned here against the production-coding-ux prototype's `.doc-tabs`
 *  strip (`docs/styles/01-core.css`), which is content-sized to ~34px -- 8px tab padding top and
 *  bottom around 12px text -- rather than the earlier 56px screenshot estimate. Px keeps the strip
 *  at its documented height on any root; the other rem geometry tokens in this file still carry the
 *  ÷16 discrepancy and are left alone here (out of scope for a tab-styling pass). */
export const STRIP_H = '34px';

/** Tab label size for both strips' tabs (`DocumentTitleNav`'s note nav / static tabs,
 *  `ReviewTransactionsTabs`' Codes/Transactions). Matches the prototype's `--fs-sm` (12px). An
 *  absolute px string for the same 14px-root reason as `STRIP_H`, and because Chakra's own `fontSize`
 *  scale would otherwise apply: an unstyled Chakra `Tab` is size `md` = `1rem` = 14px on this root,
 *  two px larger than the prototype. */
export const TAB_FONT_SIZE = '12px';

/** Resting tab label weight -- the prototype's `.doc-tab` (500). Chakra's `Tab` sets no weight at
 *  all, so without this every tab inherits the body's 400 and the active tab reads only by its
 *  border/fill. */
export const TAB_WEIGHT = 500;

/** Selected tab label weight -- the prototype's `.doc-tab.active` (600). Paired with `TAB_WEIGHT`
 *  above so selection carries a TYPE cue, not just a color one. */
export const TAB_WEIGHT_ACTIVE = 600;

/** Horizontal padding on a strip's own edge, before its first tab -- the prototype's
 *  `.doc-tabs { padding: 0 6px }`. Absolute px, not a Chakra `px={n}` scale int, which would resolve
 *  to a rem value against the 14px root (`px={3}` paints 10.5px, not the 12px its label suggests). */
export const STRIP_PAD_X = '6px';

/** Gap between adjacent tabs. The prototype's underline tabs abut at `gap: 0`; `NoteNav`'s pills
 *  need a hairline of separation so adjacent borders don't collide, so this is the smallest gap that
 *  keeps two pills visually distinct rather than the prototype's literal zero. */
export const TAB_GAP = '4px';

/** Tab pill padding -- vertical then horizontal, shared by all three strips. Tuned so a pill
 *  (padding + 12px text's ~14.4px line box + its 1px border pair) clears `STRIP_H` above with even
 *  breathing room, which `NoteNav`'s former `py={1} px={2}` (3.5px/7px on this root) did not do inside
 *  a 34px band. Deliberately TIGHTER than the prototype's `.doc-tab { padding: 8px 14px }`: an
 *  underline tab fills the strip's full height, whereas a pill floats inside it and needs room left
 *  over for its own top/bottom margin within the band. */
export const TAB_PAD_Y = '4px';
export const TAB_PAD_X = '10px';

/** Selected-tab background, shared by `NoteNav`'s pills and BOTH static tab strips
 *  (`DocumentTitleNav` / `ReviewTransactionsTabs`), so a selected tab reads the same on either side of
 *  the shell. Was hard-coded as a bare `'blue.50'` inside `NoteNav` only, which left the right pane's
 *  Codes/Transactions tabs with no fill at all. A fill PLUS the variant's underline is the prototype's
 *  own pattern, not a doubled cue -- `.doc-tab.active` sets `background: var(--surface)` alongside its
 *  `border-color: var(--primary)`; only the fill color differs here, and the palette is deliberately
 *  left as Chakra's blue rather than retuned to the prototype's white.
 *
 *  A CHAKRA SEMANTIC TOKEN, not a CSS length/color string like the rest of this file -- it is only
 *  ever handed to a Chakra `bg`/`borderColor` style prop, which resolves it against the theme
 *  palette. Do not interpolate it into a raw `style` object or a `border` shorthand template literal
 *  the way `STRIP_BORDER` et al. are used; those need a real CSS color and would render nothing. */
export const TAB_ACTIVE_BG = 'blue.50';

/** Selected-tab border color, paired with `TAB_ACTIVE_BG` above and shared by all three strips. Same
 *  "Chakra semantic token, style props only" caveat as `TAB_ACTIVE_BG`. */
export const TAB_ACTIVE_BORDER = 'blue.200';

/** Tab corner radius -- the rounded pill edge. A Chakra RADII-scale token (`md` is `0.375rem`), which
 *  is why it is a bare `'md'` rather than a CSS length: it is only handed to a `borderRadius` style
 *  prop. `NoteNav` set this inline as `borderRadius="md"`; hoisted so the static strips round
 *  identically. */
export const TAB_RADIUS = 'md';

/** Tab border width and style, as SEPARATE LONGHAND values. The border is always present and only its
 *  COLOR changes on selection (`TAB_ACTIVE_BORDER` above, `transparent` at rest), so an UNSELECTED tab
 *  shows no border at all, yet a tab does not shift by a pixel when it becomes selected.
 *
 *  DELIBERATELY NOT A `border: '1px solid'` SHORTHAND. That is what these replaced, after the
 *  shorthand painted a visible BLACK border on every unselected tab: the CSS `border` shorthand resets
 *  border-color to its initial value -- `currentColor`, i.e. the tab's own text color -- for any
 *  component it does not name, so `border` and `borderColor` conflict rather than compose. Chakra emits
 *  style props in the fixed order of its own styled-system config, NOT the order they appear on the
 *  element, so `borderColor="transparent"` cannot be relied on to win against a `border` shorthand
 *  wherever it sits in the JSX. The longhands have no such interaction -- `borderWidth`/`borderStyle`/
 *  `borderColor` each set exactly one property, so the transparent color always survives. */
export const TAB_BORDER_WIDTH = '1px';
export const TAB_BORDER_STYLE = 'solid';

/** ONE TAB IDIOM: the static Chakra `Tab`s in `DocumentTitleNav`/`ReviewTransactionsTabs` render with
 *  the SAME `TAB_PAD_*`/`TAB_BORDER`/`TAB_RADIUS`/`TAB_ACTIVE_*` pill treatment as `NoteNav`'s
 *  document tabs, rather than the prototype's flush underline tab. A short-lived earlier pass gave the
 *  static strips their own `TAB_STATIC_PAD_Y`/`TAB_STATIC_PAD_X` (8px/14px, the prototype's underline
 *  padding) while the left strip stayed pills -- two idioms in one shell, which read as a bug. Those
 *  two tokens are gone; do not reintroduce a second tab geometry without changing all three strips
 *  together. */

/** Pane footer-strip height (`PaneFooterSummary`), the outer page-footer height (`PageFooter`), and
 *  the two molecules that make up that outer footer's row (`ShortcutStatusBar`/`CaseIdentifier`) --
 *  all four share this one token instead of each hard-coding its own local
 *  `'40px'` copy, so a future retune propagates everywhere the footer row's height matters. `40px` ->
 *  `2.5rem` (÷16). */
export const FOOTER_H = '2.5rem';

/** `DocumentationPane` left rail's width, as a bare rem NUMBER -- kept LOCAL (not exported),
 *  since its only consumer is `RAIL_WIDTH` immediately below. Not exported
 *  for any numeric (non-CSS-string) consumer of its own -- there is none today -- because a bare
 *  number handed directly to a Chakra style prop is unsafe (see the file-level note above); if a
 *  genuine numeric consumer shows up later, re-export it then. Every actual consumer should use
 *  `RAIL_WIDTH` (below) for Chakra style props. `56px` -> `3.5rem` (÷16, same conversion as every
 *  other geometry token in this file). */
const RAIL_WIDTH_REM = 3.5;

/** `DocumentationPane` left rail's width, as a CSS length string -- what every Chakra width/border
 *  style prop should actually consume. */
export const RAIL_WIDTH = `${RAIL_WIDTH_REM}rem`;

// ---------------------------------------------------------------------------
// Spacing
// ---------------------------------------------------------------------------

/** `ResizableSplit`'s separator width in px -- the single source of truth for the gutter (was a
 *  locally hard-coded `DEFAULT_GUTTER_PX = 8` in `ResizableSplit.tsx`). A bare NUMBER, not a CSS
 *  string like the geometry tokens above: its only consumer, `ResizableSplitProps.gutterPx`, is
 *  typed `number` and interpolates it into a `calc(...)` template literal (`${gutterPx}px`), never
 *  hands it directly to a Chakra style prop -- so the "bare numbers resolve against Chakra's
 *  space/sizes scale" hazard documented at the top of this file does not apply to this token.
 *
 *  DELIBERATELY STILL PX, NOT REM (second styling pass known-scope exception): `ResizableSplit`'s
 *  drag/keyboard clamp math (`clampPct.ts`) measures the container via `getBoundingClientRect()`,
 *  which always returns real CSS PIXELS, and does exact pixel-precision arithmetic against
 *  `leftMinPx`/`rightMinPx`/this gutter (see `clampPct.ts`'s `CLAMP_BOUNDS_EPSILON` comment for how
 *  tightly that math is tuned -- down to float64 ULPs at exact-fit boundaries). Re-expressing the
 *  gutter as a `rem` token would only change what CSS *renders*; the clamp math would still need a
 *  live PIXEL number (either re-deriving one from `getComputedStyle(document.documentElement).
 *  fontSize` on every measurement, or accepting that the math and the paint silently disagree
 *  whenever the root font-size isn't exactly 16px). That is a real behavior change to
 *  `ResizableSplit`/`clampPct.ts` themselves, not a token-value edit, and risks the 40+ pixel-exact
 *  assertions in `ResizableSplit.test.tsx` (width-invariant, exact-fit-canonicalization, and
 *  ULP-tolerance cases) for a purely cosmetic win on an 8px separator most users will never zoom into
 *  individually. Out of scope for this pass -- left as a fixed pixel number on purpose; revisit only
 *  alongside a deliberate `ResizableSplit` change that re-derives the live px gutter from computed
 *  style. */
export const GUTTER_PX = 8;

/** Shell-level padding/gap rhythm. UNLIKE every token above, `SHELL_PAD`
 *  and `REGION_GAP` are DELIBERATELY bare Chakra spacing-SCALE numbers, not CSS px strings:
 *  `CodingScreenTemplate`'s root uses Chakra's `p`/`gap` style props for "breathing room" spacing,
 *  where a scale-relative value is the idiomatic Chakra choice -- `3` on Chakra's default scale is
 *  0.75rem (12px), squarely in `empty-ui.png`'s estimated ~10-12px rhythm -- as opposed to the
 *  FIXED, exact-pixel region heights above, which must stay raw px strings precisely to avoid that
 *  same scale-resolution hazard (see this file's header). Do not hand either of these to a prop that
 *  wants an exact px string. Already relative (Chakra resolves a scale int to a `rem` value
 *  internally) -- nothing to change here for the "dynamic units" pass above. */
/** Padding around the whole shell (`CodingScreenTemplate`'s root `p`) and a component's own edge
 *  padding (`PageFooter`'s `px`) -- both read as the same "outer padding" rhythm in `empty-ui.png`. */
export const SHELL_PAD = 3;
/** Gap between the shell's stacked regions -- header, content, footer (`CodingScreenTemplate`'s
 *  root `gap`). Same numeric value as `SHELL_PAD` today (both read the mockup's ~10-12px rhythm),
 *  kept as a DISTINCT token rather than reusing `SHELL_PAD` so padding and inter-region gap can be
 *  retuned independently later without one silently dragging the other along. */
export const REGION_GAP = 3;

// ---------------------------------------------------------------------------
// Warm neutral text + surfaces (shared)
// ---------------------------------------------------------------------------

/**
 * Warm-neutral palette shared by the patient popover (`PatientInfo`) and the Hold modal
 * (`HoldModal`, via `HOLD_COLORS`), and a ready home for `CaseIdentifier`'s duplicate hexes. biome's
 * neutrals are cool-teal-tinted (`--color-text-*` are teal #203d48/#717f96/#b0ccd6), so these WARM
 * near-black / grey / off-white values have NO faithful biome `--color-*` match -- they stay named
 * literals here (the same "no biome match -> literal" precedent `CARD_SHADOW` sets) so every molecule
 * that needs the design's warm ink draws from ONE token instead of re-inlining the same hex.
 */
/** Warm near-black -- primary text + popover-ink fills. */
export const INK = '#1a1818';
/** Warm dark-grey -- secondary text and emphasized row values. */
export const INK_SOFT = '#4a4751';
/** Warm mid-grey -- eyebrow / muted labels / secondary text (subtitle, close). */
export const INK_MUTED = '#7a7682';
/** Warm light-grey -- the faintest hints ("optional" / "select one" / placeholder), one step below
 *  INK_MUTED so hint text stays subordinate to the subtitle. */
export const INK_FAINT = '#a5a1ab';
/** Warm off-white -- raised popover surface fill (a hair warmer than the cool structural whites). */
export const SURFACE_WARM = '#f9f8fa';
/** Two-layer elevation for the raised patient popover -- heavier than `CARD_SHADOW`'s flat card lift.
 *  Same "biome has no shadow token" reasoning as `CARD_SHADOW`, so it stays a plain literal. */
export const POPOVER_SHADOW =
  '0 14px 36px -14px rgba(0, 0, 0, 0.4), 0 2px 8px -4px rgba(0, 0, 0, 0.18)';
/** Raised patient-popover corner. A dedicated 9px token (NOT the shell `CARD_RADIUS`) so the floating
 *  popover doesn't inherit future pane-card retunes, and an absolute px so it renders 9px on the app's
 *  14px root (a `rem` here would render ~12.5% small). */
export const POPOVER_RADIUS = '9px';
/** Raised patient-popover hairline border. biome `chips-grey-50`; the `#ececef` fallback preserves the
 *  popover's original border color in token-absent envs. Dedicated (not the shell `CARD_BORDER`) for
 *  the same decoupling reason as `POPOVER_RADIUS`. */
export const POPOVER_BORDER = 'var(--color-chips-grey-50, #e2e0e5)';

// ---------------------------------------------------------------------------
// Hold modal colors (CDMX-18412)
// ---------------------------------------------------------------------------

/**
 * Color tokens for the Hold modal (`molecules/HoldModal`). Grouped as one object (rather than the
 * flat `SHELL_*` consts above) so the component-specific palette stays namespaced -- consumers read
 * `HOLD_COLORS.<key>`.
 *
 * Biome CSS variables (`var(--color-<biome>, #hex)`) are used wherever the platform palette has a
 * match -- chips, alerts, hairlines all draw from biome now. The hex is CSS's own `var()` fallback
 * for environments without `ui-components/dist/index.css` (Storybook, jsdom unit tests). A few warm
 * accents (the gold pause icon, the warm off-white field fill, the near-black ink) have NO close
 * biome match -- biome's neutrals are cool-teal-tinted -- so they stay named literals to preserve
 * the approved mock. A `var(...)` reference is a plain CSS string: no import, inert to the
 * project_starburst boundary guard.
 */
export const HOLD_COLORS = {
  /** Hairline borders (inputs, dividers, pill outline). biome `chips-grey-50` (#f0f2f4). */
  border: 'var(--color-chips-grey-50, #ececef)',
  /** Close-button hover fill. biome `chips-grey-50` (#f0f2f4). */
  closeHoverBg: 'var(--color-chips-grey-50, #f0f0f2)',
  /** Pause-icon tile fill (pale cream). biome `chips-yellow-50` (#fff7d8). */
  iconBg: 'var(--color-chips-yellow-50, #fff4d6)',
  /** Selected reason/reminder chip fill + border. biome `chips-plum-500` -- the design system's own
   *  chip palette (replaces the former bespoke brand purple #8b1fc8). */
  chipSelected: 'var(--color-chips-plum-500, #875882)',
  /** Validation error text + invalid border. biome `alert-500`. */
  error: 'var(--color-alert-500, #e50000)',
  /** Gold pause bars. Nearest biome yellow-500 (#e1b129) is far; kept literal. */
  iconBar: '#c99a3a',
  /** Warm off-white field/section fill. biome neutrals are cool -- no faithful match. */
  inputBg: '#fafaf7',
  /** Note textarea hover border (a touch darker than the resting hairline). */
  noteHoverBorder: '#e0e0e4',
  /** Warm dark-grey secondary text ("Logged as"). Shared `INK_SOFT`. */
  inkSoft: INK_SOFT,
  /** Near-black primary text + popover fill. Shared `INK`. */
  ink: INK,
  /** Warm mid-grey -- subtitle + close icon. Shared `INK_MUTED`. */
  inkMuted: INK_MUTED,
  /** Warm light-grey -- the faint field hints ("optional" / "select one"). Shared `INK_FAINT`. */
  inkFaint: INK_FAINT
} as const;

// ---------------------------------------------------------------------------
// Case-action modal radii (CDMX-18412 Hold, Transfer, ...)
// ---------------------------------------------------------------------------

/**
 * Corner radii shared by every case-action modal + its trigger. Grouped like `HOLD_COLORS` so the
 * rounding lives in one place instead of scattered raw literals -- and so a trigger icon tile and the
 * modal's inputs can't silently drift apart (they were `8px` vs `0.5rem` for the same corner before
 * this). ABSOLUTE PX, not rem: the app forces a 14px root (`src/styles/cmx.scss`), so a `rem` radius
 * renders ~12.5% smaller than its label; px keeps every corner at its documented size on any root.
 *
 * Named `HOLD_RADIUS` while Hold was the only case action; renamed for so the SECOND
 * action (Transfer) draws from a neutral token instead of importing Hold's -- an inverted
 * Transfer->Hold dependency the design review (finding A9) called out. Pure rename:
 * identical values, and `HoldModal`/`HoldAction` updated in place.
 */
export const CASE_ACTION_RADIUS = {
  /** Modal outer corner. */
  modal: '14px',
  /** Header icon tile / raised inner tile. */
  tile: '9px',
  /** Inputs, note, hover popover, trigger icon -- the shared control corner. */
  control: '8px',
  /** Small square controls (close button). */
  small: '6px'
} as const;

// ---------------------------------------------------------------------------
// Chip colors (the "Day X of Y" chip and its concurrent-stay variant)
// ---------------------------------------------------------------------------

/**
 * Color tokens for the two-tone (`primary`/`warn`) `Chip` pill primitive (`molecules/Chip`) AND the
 * domain content `Encounter` (`molecules/Encounter`) composes on top of it -- Chip itself is generic
 * and only consumes `primary`/`primaryFaint`/`primarySoft`/`surface`/`warn`/`warnFaint`/
 * `warnBorderSoft` for its border/hover accent; `Encounter` reaches into the same object directly for
 * its icon-badge fill, "Day X of Y" text, open-glyph hover color, and concurrent-stay segment (the
 * `warnGlow` shared-billing-codes dot). Grouped as one object, `HOLD_COLORS`-style, so the two
 * palettes (default purple, concurrent-stay amber) stay namespaced together across both consumers.
 *
 * `primary`/`primaryFaint` reuse the exact fallback hex already established informally elsewhere in
 * this folder for the same purple -- `CaseSpecialty.tsx`'s edit-button hover uses
 * `var(--primary, #6b4bd4)` verbatim, and its `ROLE_PRIMARY` badge uses `var(--ai, #6b4bd4)` /
 * `var(--ai-faint, #efeafc)`. No biome token is this purple family; `--primary`/`--primary-faint` are
 * the chip's own hand-off names for the same bespoke accent, so they get their own `var(...)` + hex
 * fallback pair rather than reusing `--ai` under this component's nose. `warn`/`warnFaint` map to
 * REAL biome tokens (`--color-warning-500` #f5a73f / `--color-warning-300` #ffe9cc) since the warning
 * family has a faithful match, unlike purple.
 */
export const CHIP_COLORS = {
  /** Icon-badge fill, "Day X of Y" text, resting-hover border, and the open glyph's hover color. */
  primary: 'var(--primary, #6b4bd4)',
  /** Pale purple -- the pill's gradient near end and its solid hover background. */
  primaryFaint: 'var(--primary-faint, #efeafc)',
  /** Soft purple resting border -- one step darker than `primaryFaint`, lighter than `primary`. No
   *  biome match for this exact mid-tone; kept a literal fallback, same "no match -> literal"
   *  precedent as `HOLD_COLORS.iconBar`. */
  primarySoft: 'var(--primary-soft, #efe1f8)',
  /** Pill background's gradient far end -- plain white. The same white `CARD_BG`/`BAR_BG` already
   *  render (biome `--color-structural-0`), under this chip's own hand-off name (`--surface`) rather
   *  than a biome token, since the two families in this object should read as one design, not one
   *  bespoke pair plus one biome reference. */
  surface: 'var(--surface, #ffffff)',
  /** `.has-siblings` accent -- icon/border/text color once concurrent stays exist. biome
   *  `--color-warning-500` (#f5a73f). */
  warn: 'var(--warn, #f5a73f)',
  /** `.has-siblings` hover background, and the pale halo behind the shared-billing-codes dot. biome
   *  `--color-warning-300` (#ffe9cc). */
  warnFaint: 'var(--warn-faint, #ffe9cc)',
  /** `.has-siblings` RESTING border -- `--warn` mixed 50% into transparent (the hand-off's own "amber
   *  at 50% opacity"), so the pill's own background shows through rather than a flat pale swatch. */
  warnBorderSoft: 'color-mix(in srgb, var(--warn, #f5a73f) 50%, transparent)',
  /** Soft glow ring around the shared-billing-codes dot (`.chip-sib-dot`). No biome
   *  elevation/glow token, same "no match -> literal" precedent as `CARD_SHADOW`/`POPOVER_SHADOW`. */
  warnGlow: '0 0 0 3px rgba(245, 167, 63, 0.3)'
} as const;

// ---------------------------------------------------------------------------
// Encounter metric badge colors (Aging/Codable Days pill in the Encounter popover)
// ---------------------------------------------------------------------------

/**
 * Color tokens for the small rounded "Nd" badge the Encounter popover renders for Aging Days and
 * Codeable Days -- a coral/red urgency pill, distinct from the purple stay `CHIP_COLORS` family. biome
 * `chips-red-50`/`chips-red-500` are an exact match for the approved pill (light pink fill, coral-red
 * bold text), unlike `CHIP_COLORS`'s purple/amber pair which has no red option.
 */
export const ENCOUNTER_METRIC_COLORS = {
  /** Badge fill. biome `--color-chips-red-50` (#ffedec). */
  bg: 'var(--color-chips-red-50, #ffedec)',
  /** Badge text. biome `--color-chips-red-500` (#c83729). */
  text: 'var(--color-chips-red-500, #c83729)'
} as const;

// ---------------------------------------------------------------------------
// Sibling-case colors (the "Concurrent cases" list in the Encounter popover)
// ---------------------------------------------------------------------------

/**
 * Color tokens for each concurrent-case row's status badge (`SiblingCase.state`), grouped by state
 * so `Encounter` can look one up with a single `SIBLING_STATUS_COLORS[state]` instead of a switch.
 * `done`/`auto` share one treatment (both read as "already resolved"); `queued` is the deliberate
 * fallback for any state value this map doesn't recognize (an unfamiliar string from a future
 * backend enum should read as neutral, not throw or render unstyled).
 */
export const SIBLING_STATUS_COLORS = {
  /** biome `--color-success-500` (#42a45c) / `--color-success-300` (#ccffda) -- resolved states. */
  done: {
    text: 'var(--color-success-500, #42a45c)',
    bg: 'var(--color-success-300, #ccffda)'
  },
  auto: {
    text: 'var(--color-success-500, #42a45c)',
    bg: 'var(--color-success-300, #ccffda)'
  },
  /** Reuses the `--ai`/`--ai-faint` fallback pair `CaseSpecialty`/`CareTeam` already established for
   *  this purple "in progress right now" accent -- no biome token for it, same bespoke-purple
   *  reasoning as `CHIP_COLORS.primary`. */
  current: { text: 'var(--ai, #6b4bd4)', bg: 'var(--ai-faint, #efeafc)' },
  /** Neutral fallback -- `INK_MUTED` over the `--panel-2` fill `CaseSpecialty`/`CareTeam` already use
   *  for their own muted badges. */
  queued: { text: INK_MUTED, bg: 'var(--panel-2, #eceef2)' }
} as const;

/**
 * Row-chrome + text-ladder tokens shared by every concurrent-case row: the row card's own surface
 * (one step lighter than the popover's `SURFACE_WARM`, reusing `CHIP_COLORS.surface`'s plain-white
 * fallback rather than inventing a third near-white), the role badge's muted pill, and the
 * providers/E-M-description tier of the ink ladder (one step lighter than `INK_MUTED`, i.e.
 * `INK_FAINT`).
 */
export const SIBLING_ROW_COLORS = {
  /** Row card background -- plain white against the popover's warm off-white. */
  surface: CHIP_COLORS.surface,
  /** Role badge fill, shared with the dx-code chip's resting fill. */
  panel2: 'var(--panel-2, #eceef2)',
  /** Role badge text + dx-code chip text. */
  roleText: INK_SOFT,
  /** Providers line + E/M description text -- the lightest tier, one step below `INK_MUTED`. */
  detailText: INK_FAINT
} as const;

/** Concurrent-case row's monospace stack, shared by the E/M code and every dx-code chip -- matches
 *  `MetricBadge`'s numeric-badge intent (a code should read as code) without pulling in a new font
 *  family per component. */
export const MONO_STACK = '"JetBrains Mono", monospace';

/** Sans-serif stack shared by `FlagsInfo`'s chip label, `PatientInfo`'s trigger, and
 *  `PatientTypeInfo`'s trigger -- centralized here so the three don't each hand-roll their own
 *  slightly different Inter fallback list. */
export const SANS_STACK = "'Inter', system-ui, sans-serif";

// ---------------------------------------------------------------------------
// Provider-details drawer geometry (Encounter's provider-details Drawer)
// ---------------------------------------------------------------------------

/**
 * Padding/position for `Encounter`'s provider-details `Drawer` (header/body/close-button).
 * `headerX`/`bodyX` are Chakra's own STOCK `DrawerHeader`/`DrawerBody` inline (horizontal) padding
 * -- scale step `6` (`1.5rem`) -- resolved against this app's forced 14px root
 * (`src/styles/cmx.scss`): `1.5rem * 14px = 21px`. `project_starburst` cannot import
 * `@codametrix/themes` to inherit that resolution directly -- same "isolated shell can't assume the
 * ambient theme" reasoning every other token in this file follows -- so it's hardcoded here as the
 * literal px it actually renders to. Grouped as ONE object (not separate literals) specifically so
 * `headerX`/`bodyX` share a single source: header and body content must land on the exact same left
 * edge, and two independently-typed `'21px'` strings could silently drift apart on a future edit
 * where one literal changes and the other doesn't.
 */
export const PROVIDER_DRAWER_GEOMETRY = {
  /** Header's own horizontal (inline) padding -- SAME value as `bodyX` on purpose (see above). */
  headerX: '21px',
  headerTop: '40px',
  /** Header's own bottom padding -- the OUTER of the two nested bottom-paddings; `.provider-header-
   *  section`'s further `padding-bottom: 8px` (`provider-details.scss`) is the inner one, applied
   *  separately to the wrapping `Box` around the header's content. */
  headerBottom: '20px',
  /** The wrapping `Box`'s own bottom padding, INSIDE the header cell, before its border-bottom --
   *  mirrors `.provider-header-section { padding-bottom: $spacing-2x }` (8px) exactly. */
  headerContentBottom: '8px',
  /** Body's own horizontal (inline) padding -- SAME value as `headerX` (see above). */
  bodyX: '21px',
  bodyTop: '0px',
  bodyBottom: '40px',
  /** `DrawerCloseButton`'s offset from the top-right corner. */
  closeButtonInset: '28px'
} as const;

// ---------------------------------------------------------------------------
// Transfer modal colors
// ---------------------------------------------------------------------------

/**
 * Color tokens for the Transfer action (`molecules/TransferAction` / `molecules/TransferModal`),
 * namespaced exactly like `HOLD_COLORS`. Structural neutrals (hairline, error, field fill, the warm
 * ink ramp) are deliberately the SAME values Hold uses -- the two modals are the same chrome with a
 * different accent -- so they reference the shared consts rather than re-inlining hexes.
 *
 * The accent is the ticket screenshot's pale-mint tile + dark-green north-east arrow, mapped onto
 * biome's own green chip ramp where it has a match.
 */
export const TRANSFER_COLORS = {
  /** Hairline borders (inputs, dividers, pill outline). Same hairline as Hold. */
  border: 'var(--color-chips-grey-50, #ececef)',
  /** Close-button hover fill. */
  closeHoverBg: 'var(--color-chips-grey-50, #f0f0f2)',
  /** Transfer-icon tile fill (pale mint). biome `chips-green-50`. */
  iconBg: 'var(--color-chips-green-50, #dfeee4)',
  /** The north-east arrow glyph stroke (dark green). biome `chips-green-700`. */
  iconArrow: 'var(--color-chips-green-700, #1f5c46)',
  /** Selected reason chip fill + border. Shared with Hold's chip so the two modals match. */
  chipSelected: 'var(--color-chips-plum-500, #875882)',
  /** Validation error text + invalid border. biome `alert-500`. */
  error: 'var(--color-alert-500, #e50000)',
  /** Warm off-white field/section fill. */
  inputBg: '#fafaf7',
  /** Textarea hover border (a touch darker than the resting hairline). */
  noteHoverBorder: '#e0e0e4',
  /** Combobox result row -- hover/active fill (the screenshot's pale highlight). */
  optionActiveBg: 'var(--color-chips-green-50, #eef6f1)',
  /** Combobox group header band ("PEOPLE · 4"). */
  groupHeaderBg: '#f4f4f6',
  /** Warm dark-grey secondary text. Shared `INK_SOFT`. */
  inkSoft: INK_SOFT,
  /** Near-black primary text. Shared `INK`. */
  ink: INK,
  /** Warm mid-grey -- subtitle + close icon. Shared `INK_MUTED`. */
  inkMuted: INK_MUTED,
  /** Warm light-grey -- the faint field hints ("optional" / "required"). Shared `INK_FAINT`. */
  inkFaint: INK_FAINT
} as const;

// ---------------------------------------------------------------------------
// Exclude modal colors
// ---------------------------------------------------------------------------

/**
 * Color tokens for the Exclude action, namespaced like `HOLD_COLORS` above.
 *
 * Each `var()` carries its REAL token value as the fallback — a fallback that differs from its token makes
 * Storybook show something production will not.
 *
 * The warn/danger keys are plain literals rather than biome tokens because the obvious picks fail WCAG
 * 1.4.3: `chips-brown-500` (#c37c6a) measures 2.91:1 as body text on `chips-brown-50` and 3.28:1 under a
 * white label, and the family has no darker step. Measured ratios are recorded per key.
 */
export const EXCLUDE_COLORS = {
  /** Hairline borders (inputs, dividers, pill outline). Mirrors `HOLD_COLORS.border`. */
  border: 'var(--color-chips-grey-50, #f0f2f4)',
  /** Close-button hover fill. */
  closeHoverBg: 'var(--color-chips-grey-50, #f0f2f4)',
  /** Validation error text + invalid border. biome `alert-500`. */
  error: 'var(--color-alert-500, #e50000)',
  /** Selected reason chip fill + border. biome `chips-plum-500`; white label on it measures 5.60:1. */
  chipSelected: 'var(--color-chips-plum-500, #875882)',
  /** Warm off-white field/section fill. biome neutrals are cool -- no faithful match (as HOLD_COLORS). */
  inputBg: '#fafaf7',

  // -- Exclude glyph tile ---------------------------------------------------
  /** Trigger + modal-header tile fill. biome `chips-plum-50`. NOTE: the approved mock's tile is a
   *  lavender-blue (#efedf8) and biome's plum-50 is a warmer pink-plum -- there is no indigo/lavender family
   *  in the palette, so this DELIBERATELY renders plum rather than carrying a mismatched fallback. Recorded
   *  as a reviewed look change in the design, not a slip. */
  iconBg: 'var(--color-chips-plum-50, #f3eaf2)',
  /** Circle-slash stroke. biome `chips-plum-500`; measures 4.77:1 on `iconBg`, comfortably over the 3:1
   *  WCAG 1.4.11 asks of a meaningful graphic. */
  iconStroke: 'var(--color-chips-plum-500, #875882)',

  // -- "Heavy action" warning banner ---------------------------------------
  /** Banner fill. biome `chips-brown-50`. */
  warnBg: 'var(--color-chips-brown-50, #feeeea)',
  /** Banner hairline. biome `chips-brown-500` -- a DIFFERENT token from `warnBg` (see rule 2 above), so the
   *  edge is real in production. 2.91:1 against the fill: ample for a decorative container edge, which is
   *  not a UI component and so is out of WCAG 1.4.11's scope. */
  warnBorder: 'var(--color-chips-brown-500, #c37c6a)',
  /** Banner text + triangle glyph. Literal: measures **6.60:1** on `warnBg`. biome's own `chips-brown-500`
   *  would be 2.91:1 and fail WCAG 1.4.3. */
  warnInk: '#8a3f28',

  // -- Destructive primary button ------------------------------------------
  /** "Exclude and Archive" fill. Literal: white label on it measures **5.25:1** (PASS). biome
   *  `chips-brown-500` would be 3.28:1 and fail. */
  danger: '#a8543c',
  /** Its hover fill -- a genuinely darker value, not the same token twice. White on it: **6.93:1**. */
  dangerHover: '#8d4531',

  // -- Shared warm-ink scale (identical to HOLD_COLORS) ---------------------
  /** Warm dark-grey secondary text ("Logged as"). Shared `INK_SOFT`; 8.0:1 on white. */
  inkSoft: INK_SOFT,
  /** Near-black primary text. Shared `INK`. */
  ink: INK,
  /** Subtitle + close icon; 5.19:1 on white. */
  inkMuted: '#6f6b77',
  /** Faint field hints; 4.67:1 on white. Lighter than `inkMuted` so the hierarchy stays monotonic —
   *  a darker value passes contrast but inverts it. */
  inkFaint: '#767281',
  /** Foreground on `danger` and on a checked reason pill. Named here rather than inlined as `'white'` in
   *  the component because the measured contrast ratios above (5.25:1 for `danger`, 5.60:1 for
   *  `chipSelected`) are ratios AGAINST THIS VALUE -- leaving it outside the palette would put the number
   *  that validates the palette somewhere the palette cannot see. */
  onFilled: '#ffffff'
} as const;

// ---------------------------------------------------------------------------
// Exclude geometry
// ---------------------------------------------------------------------------

/**
 * Geometry for the Exclude glyph + banner. Named here rather than inlined in the components, per the
 * folder's "chrome lives in styles/tokens.ts -- nowhere else" invariant. Absolute px for the same reason
 * `HOLD_RADIUS` documents: the app forces a 14px root (`src/styles/cmx.scss`), so a `rem` length renders
 * ~12.5% smaller than its label.
 */
export const EXCLUDE_SIZES = {
  /** Patient-summary-bar trigger tile. Matches HoldAction's trigger, which is also a literal `28px`. */
  triggerTile: '28px',
  /**
   * Modal header tile. NOT the same rendered size as Hold's, and deliberately so: `HoldModal` uses
   * `boxSize="2.75rem"`, which under the 14px root this file exists to work around renders **38.5px**, not
   * the 44px its own comment claims. This is the corrected value; bringing Hold into line is a follow-up
   * rather than a change to a merged ticket's file. Until then the two modals' header tiles differ by
   * 5.5px, which is a real visual inconsistency and is recorded as such.
   */
  headerTile: '44px'
} as const;

/**
 * Glyph dimensions as BARE NUMBERS, kept in a separate object on purpose. These feed `<svg width|height>`,
 * which wants a number — but the folder's rule is that every length handed to a Chakra style prop must be a
 * string, because Chakra resolves a bare number against its spacing scale (`boxSize={24}` is `6rem`, not
 * 24px). Splitting them means nobody can reach for one of these and accidentally get a scale lookup.
 */
export const EXCLUDE_GLYPH_PX = {
  /** Drawn inside the trigger tile. */
  trigger: 16,
  /** Drawn inside the modal header tile. */
  header: 24,
  /** The warning-banner triangle. */
  warn: 16
} as const;

// ---------------------------------------------------------------------------
// Flag chip colors (the Case Context Bar's Flags field)
// ---------------------------------------------------------------------------

/**
 * Color tokens for the flag chips (`molecules/FlagsInfo`) inside the Case Context Bar's Flags
 * field, keyed by tone. `strong` reuses `CHIP_COLORS.primary`/`primaryFaint`/`primarySoft` (the
 * bespoke purple `CaseSpecialty` already hands off as `--ai`/`--ai-faint`); `info` reuses the same
 * neutral grey + `INK_SOFT` pairing `CaseContextBar`'s own label segment (`FIELD_LABEL_BG`) already
 * uses, bordered with the shared `CARD_BORDER` hairline. `warn`'s fill is a dedicated literal,
 * `#FDF9ED` (explicit product direction) -- a warm pale cream distinct from biome's own
 * `--color-warning-300` (`#ffe9cc`, a peach), so it does NOT reuse `CHIP_COLORS.warnFaint`; its
 * text/border still reuse `CHIP_COLORS.warn`/`warnBorderSoft` (biome `--color-warning-500` plus the
 * chip primitive's own 50%-mixed border), which already read fine against the new fill. Grouped as
 * one object, `CHIP_COLORS`-style, so a consumer reads `FLAG_CHIP_COLORS[tone]` instead of a switch.
 * All three tones are defined even though today's mapper (`toFlagsInfoFromCaseRun`) only ever emits
 * `warn` -- see that function's own doc comment for why.
 */
/** `CaseContextBar`'s `'compact'` `valueChrome` segment padding (`FieldItem.valueChrome`) -- tighter
 *  than the bar's uniform `'0 10px'`, for a value that already carries its own inner padding/height
 *  (today: `FlagsInfo`'s 18px-tall chip strip). Split label/value like `FIELD_LABEL_BG`/
 *  `FIELD_VALUE_BG` above since the two segments' insets differ (the label still needs room for its
 *  uppercase text, the value only needs to clear the chip strip's own padding). Together with the
 *  chip strip's own 18px chip height, the value segment lands at 18 + 3 + 3 = 24px content height,
 *  which `CaseContextBar`'s `alignItems="stretch"` then applies to the label segment too, landing
 *  the whole pill (content + 1px border pair) at 26px -- the target height for a chip-carrying
 *  field's pill. */
export const COMPACT_FIELD_LABEL_PAD = '5px 9px 5px 10px';
export const COMPACT_FIELD_VALUE_PAD = '3px 7px';

export const FLAG_CHIP_COLORS = {
  strong: {
    bg: CHIP_COLORS.primaryFaint,
    fg: CHIP_COLORS.primary,
    border: CHIP_COLORS.primarySoft
  },
  warn: {
    bg: '#FDF9ED',
    fg: CHIP_COLORS.warn,
    border: CHIP_COLORS.warnBorderSoft
  },
  info: { bg: 'var(--panel-2, #eceef2)', fg: INK_SOFT, border: CARD_BORDER }
} as const;

/** `FlagsInfo`'s chip LABEL text only (`flags-info-chip-label`) -- a dedicated literal per explicit
 *  product direction, distinct from `FLAG_CHIP_COLORS[tone].fg`, which still governs the chip's
 *  monospace glyph/chevron. No biome match for this exact amber-brown, same "no match -> literal"
 *  precedent as `FLAG_CHIP_COLORS.warn`'s own fill. */
export const FLAG_CHIP_LABEL_FG = '#b08432';

// ---------------------------------------------------------------------------
// Routing Context (the case-context bar's right-pinned "why routed" tag)
// ---------------------------------------------------------------------------

/** `RoutingContext`'s chip colors. `#8b1fc8` is the reference's own `--primary` value, kept as a
 *  dedicated literal per explicit product direction -- NOT `CHIP_COLORS.primary`, whose `--primary`
 *  fallback is the different `#6b4bd4`. `fg` is the text; `bg` is a faint wash of the same purple;
 *  `border` reuses `CHIP_COLORS.primarySoft` (`var(--primary-soft)`). */
export const ROUTING_CONTEXT_COLORS = {
  fg: '#8b1fc8',
  bg: 'color-mix(in srgb, #8b1fc8 8%, transparent)',
  border: CHIP_COLORS.primarySoft
} as const;

/** `RoutingContext`'s tag typography, verbatim from the reference's `.routed-inline-tag`. PX, not
 *  rem, for the 14px-root reason `CASE_ACTION_RADIUS` documents. */
export const ROUTING_CONTEXT_FONT_SIZE = '11px';
export const ROUTING_CONTEXT_FONT_WEIGHT = 700;
export const ROUTING_CONTEXT_LETTER_SPACING = '0.07em';

// ---------------------------------------------------------------------------
// Vertical divider (`molecules/VerticalDivider`)
// ---------------------------------------------------------------------------

/** Fill of the reusable `VerticalDivider` -- the thin rule `PatientSummaryBar` draws between the
 *  info fields inside its `patient-summary-info` chip, now shared with the case-context bar. */
export const VERTICAL_DIVIDER_COLOR = 'var(--border-strong, #c9c8ce)';

/** Default `VerticalDivider` geometry, verbatim from that same patient-summary rule. PX, not rem, for
 *  the 14px-root reason `PATIENT_BAR_H` documents. */
export const VERTICAL_DIVIDER_W = '1px';
export const VERTICAL_DIVIDER_H = '13px';
export const VERTICAL_DIVIDER_MX = '2px';
